import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ChevronLeft, Users, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SpecialistGroupsPage({ onBack }) {
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/groups/campers`);
        setGroups(res.data.groups || {});
      } catch (_e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Exclude CIT groups; sort by group code
  const groupList = useMemo(() => {
    return Object.entries(groups)
      .filter(([code]) => !code.toUpperCase().startsWith("CIT"))
      .map(([code, campers]) => ({ code, campers, count: campers.length }))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [groups]);

  const totalKids = useMemo(
    () => groupList.reduce((n, g) => n + g.count, 0),
    [groupList]
  );

  // ── Group detail view ──
  if (selectedGroup) {
    const group = groupList.find(g => g.code === selectedGroup);
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-10 bg-white border-b shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedGroup(null)}
              data-testid="specialist-groups-back-list"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              All Groups
            </Button>
            <div className="ml-auto flex items-center gap-2 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-3 py-1 text-sm font-bold">
              <Users className="w-4 h-4" />
              {group?.count ?? 0}
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 py-6">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-4">
            Group {group?.code}
          </h2>
          {group && group.campers.length > 0 ? (
            <ul className="grid gap-2 sm:grid-cols-2">
              {group.campers.map((c, i) => (
                <li
                  key={`${c.first_name}-${c.last_name}-${i}`}
                  className="bg-white border rounded-lg px-3 py-2.5 text-sm text-gray-900 flex items-center gap-2"
                  data-testid={`specialist-groups-camper-${group.code}-${i}`}
                >
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
                  </span>
                  <span className="truncate">
                    {c.first_name} {c.last_name}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center text-muted-foreground py-16">No campers enrolled.</p>
          )}
        </main>
      </div>
    );
  }

  // ── Groups list view ──
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            data-testid="specialist-groups-back-home"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="ml-auto flex items-center gap-2 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-3 py-1 text-sm font-bold">
            <Users className="w-4 h-4" />
            {totalKids} total
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold text-gray-900">Groups</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Tap a group to see the full camper roster
          </p>
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-16">Loading groups…</p>
        ) : groupList.length === 0 ? (
          <p className="text-center text-muted-foreground py-16">No groups found.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {groupList.map(g => (
              <button
                key={g.code}
                type="button"
                onClick={() => setSelectedGroup(g.code)}
                className="bg-white border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-xl px-4 py-3 flex items-center justify-between transition-all text-left group"
                data-testid={`specialist-groups-tile-${g.code}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-emerald-700">{g.code}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Group {g.code}</p>
                    <p className="text-xs text-muted-foreground">{g.count} camper{g.count !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-emerald-500 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
